import { Controller } from 'stimulus'
import { useDebounce } from 'stimulus-use'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import { getCsrfParamsAsObject } from '~/common/helpers/csrf'

export default class extends Controller {
  static values = {
    priceChangedUrl: String,
    quantityChangedUrl: String,
    addToCartUrl: String,
    selectedPriceId: String,
    selectedQuantity: Number,
  }

  static debounces = [
    { name: 'updateCartItemQuantity', wait: 400 },
    { name: 'rerenderPrices', wait: 300 },
  ]

  static targets = ['loadingOverlay', 'buyNowLink']

  connect() {
    useDebounce(this)
  }

  showLoadingOverlay() {
    this.loadingOverlayTarget.classList.remove('hidden')
  }

  hideLoadingOverlay() {
    this.loadingOverlayTarget.classList.add('hidden')
  }

  // Runs synchronously on every price click. Records the latest price and opens a
  // "price update in progress" promise that addToCart/buyNow can await.
  onPriceChanged({ detail }) {
    this.latestPriceId = detail.priceId

    if (!this.priceUpdatePromise) {
      this.priceUpdatePromise = new Promise((resolve) => {
        this.resolvePriceUpdate = resolve
      })
    }

    this.rerenderPrices()
  }

  // Debounced. The response is morphed in (see ProductsController#price_changed), so the
  // price change applies without a flicker — hence no loading overlay.
  async rerenderPrices() {
    const priceId = this.latestPriceId

    try {
      const response = await fetch(this.priceChangedUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          price_id: priceId,
          ...getCsrfParamsAsObject(),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // A different price was clicked while this request was in flight — ignore the stale response.
      if (this.latestPriceId !== priceId) return

      await this.handleResponse(response)
    } catch (error) {
      if (this.latestPriceId !== priceId) return

      AjaxHelper.doUpdate({ alert: this.getStandardErrorMessage() })
    } finally {
      // Once the latest price's update has settled, release any held addToCart/buyNow clicks.
      if (this.latestPriceId === priceId) {
        this.resolvePriceUpdate?.(priceId)
        this.priceUpdatePromise = null
        this.resolvePriceUpdate = null
      }
    }
  }

  async updateCartItemQuantity(e) {
    try {
      this.showLoadingOverlay()
      const response = await fetch(this.quantityChangedUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          price_id: this.selectedPriceIdValue,
          quantity: e.target.value,
          ...getCsrfParamsAsObject(),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      this.handleResponse(response)
    } catch (error) {
      AjaxHelper.doUpdate({ alert: this.getStandardErrorMessage() })
    } finally {
      this.hideLoadingOverlay()
    }
  }

  async addToCart() {
    // Hold the click until any pending price change has fully applied, so we add the right price.
    const priceId = this.priceUpdatePromise
      ? await this.priceUpdatePromise
      : this.selectedPriceIdValue

    try {
      this.showLoadingOverlay()
      const response = await fetch(this.addToCartUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          price_id: priceId,
          quantity: this.selectedQuantityValue,
          ...getCsrfParamsAsObject(),
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      this.handleResponse(response)
    } catch (error) {
      AjaxHelper.doUpdate({ alert: this.getStandardErrorMessage() })
    } finally {
      this.hideLoadingOverlay()
    }
  }

  // Buy now is a plain link. If a price change is still settling, hold the navigation
  // until it has applied, then go to the now up-to-date URL.
  async buyNow(e) {
    if (!this.priceUpdatePromise) return

    e.preventDefault()
    await this.priceUpdatePromise
    window.location.href = this.buyNowLinkTarget.href
  }

  async handleResponse(response) {
    if (response.ok) {
      const data = await response.json()
      AjaxHelper.doUpdate(data)

      return
    }

    AjaxHelper.doUpdate({ alert: this.getStandardErrorMessage() })
  }

  getStandardErrorMessage() {
    return 'Something went wrong. Please refresh the page and try again later.'
  }
}
